import React, { useState, useCallback } from "react";
import "../styles/executive-responsive.css";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useProjects } from "../../common/contexts/ProjectContext";
import { useProjectNavigation } from "../../../hooks/useProjectNavigation";
import { ParticipantList } from "../views/ParticipantList";
import { ParticipantSummaryDetail } from "../views/ParticipantSummaryDetail";
import { ProjectRollingSummaryView } from "../views/ProjectRollingSummaryView";
import { RoleBrandingHeader } from "../../common/components/RoleBrandingHeader";
import { UserMenu } from "../../common/components/UserMenu";
import { BugReportButton } from "../../common/components/BugReportButton";
import { LanguageToggle } from "../../common/components/LanguageToggle";
import { FullscreenButton } from "../../common/components/FullscreenButton";
import { ButtonGroup } from "@/components/ui/button-group";
import { ConnectionStatusWidget } from "../../common/components/ConnectionStatusWidget";

type ViewMode = "project" | "characters";

export const ExecutiveLayout: React.FC = () => {
  const { t } = useTranslation();
  const { activeProject, clearActiveProject } = useProjects();
  const { navigate } = useProjectNavigation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedParticipant, setSelectedParticipantObj] = useState<any>(null);

  // Derive view mode from URL (?view=project | ?view=characters)
  const urlMode = searchParams.get("view") as ViewMode | null;
  const viewMode: ViewMode =
    urlMode === "project" || urlMode === "characters" ? urlMode : "project";

  // Derive selected participant from URL (?participant=...)
  const rawParticipantId = searchParams.get("participant") || null;
  const isValidUUID =
    rawParticipantId &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawParticipantId);
  const selectedParticipantId = isValidUUID ? rawParticipantId : null;

  const setSelectedParticipant = useCallback(
    (idOrObj: string | any | null) => {
      const id = idOrObj && typeof idOrObj === "object" ? idOrObj.id : idOrObj;
      if (id) {
        setSearchParams({ view: viewMode, participant: id }, { replace: true });
      } else {
        setSearchParams({ view: viewMode }, { replace: true });
        setSelectedParticipantObj(null);
      }
    },
    [setSearchParams, viewMode],
  );

  const handleSelectParticipant = useCallback(
    (p: any) => {
      setSelectedParticipantObj(p);
      const participantId = typeof p === "string" ? p : p?.id;
      setSearchParams({ view: viewMode, participant: participantId }, { replace: true });
    },
    [setSearchParams, viewMode],
  );

  if (!activeProject) return null;

  const modeButtons: { value: ViewMode; labelKey: string; icon: string }[] = [
    { value: "project", labelKey: "executive.modeProject", icon: "bx-folder" },
    { value: "characters", labelKey: "executive.modeCharacters", icon: "bx-group" },
  ];

  return (
    <div
      className="executive-layout"
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--color-bg-mesh, #0f0f0f)",
        color: "var(--color-text-primary)",
        fontFamily: "var(--font-family)",
      }}
    >
      <header
        className="executive-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "18px 28px",
          borderBottom: "1px solid var(--color-panel-border)",
          background: "rgba(255,255,255,0.02)",
          flexShrink: 0,
        }}
      >
        <RoleBrandingHeader
          role="executive"
          roleLabel={t("executive.title").toUpperCase()}
          projectName={activeProject.name}
          onHomeClick={() => {
            clearActiveProject();
            navigate("/projects");
          }}
          testIdPrefix="executive"
        />
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <ButtonGroup>
            <LanguageToggle />
            <BugReportButton module="executive" />
            <FullscreenButton isInline={true} />
          </ButtonGroup>
          <ConnectionStatusWidget />
          <UserMenu role="executive" testIdPrefix="executive-user" />
        </div>
      </header>

      {/* Sub-header: mode toggle — hidden when viewing a participant detail */}
      {!selectedParticipantId && (
        <div
          className="executive-mode-toggle"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "14px 28px",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
            background: "rgba(255,255,255,0.015)",
            flexShrink: 0,
          }}
        >
          {modeButtons.map((btn) => {
            const active = viewMode === btn.value;
            return (
              <button
                key={btn.value}
                className="executive-mode-btn"
                onClick={() => {
                  setSearchParams({ view: btn.value }, { replace: true });
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: active
                    ? "linear-gradient(135deg, #EC4899, #DB2777)"
                    : "rgba(255,255,255,0.04)",
                  color: active ? "#fff" : "var(--color-text-muted)",
                  border: `1px solid ${active ? "transparent" : "rgba(255,255,255,0.1)"}`,
                  padding: "8px 20px",
                  borderRadius: "var(--border-radius-pill, 9999px)",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  transition: "all 0.25s ease",
                  fontFamily: "inherit",
                  boxShadow: active ? "0 2px 12px rgba(236,72,153,0.25)" : "none",
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                  }
                }}
              >
                <i className={`bx ${btn.icon}`} style={{ fontSize: 16 }} />
                {t(btn.labelKey)}
              </button>
            );
          })}
        </div>
      )}

      {/* Scrollable content area */}
      <main
        className="executive-main"
        style={{
          flex: 1,
          overflow: "auto",
          padding: "24px 28px",
        }}
      >
        {selectedParticipantId ? (
          <ParticipantSummaryDetail
            participantId={selectedParticipantId}
            participantData={selectedParticipant}
            projectId={activeProject.id}
            onBack={() => setSelectedParticipant(null)}
          />
        ) : viewMode === "project" ? (
          <ProjectRollingSummaryView projectId={activeProject.id} />
        ) : (
          <ParticipantList
            projectId={activeProject.id}
            onSelect={handleSelectParticipant}
            summaryType="daily"
          />
        )}
      </main>
    </div>
  );
};

export default ExecutiveLayout;
